'use client'

import { useLazyQuery } from '@apollo/client/react'
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useCart } from '../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../app/_providers/ExperimentationProvider.client'
import { useGraphqlClients } from '../../app/_providers/GraphqlClientsProvider.client'
import { useOrganization } from '../../app/_providers/OrganizationProvider.client'
import { EMBROIDERY_UPSELL_ATTRIBUTE_KEY } from '../../brunswick/containers/cart'
import { EmbroideryModal } from '../../components/embroidery/EmbroideryModal/EmbroideryModal'
import { FETCH_CART_EMBROIDERY_CONFIG } from '../../lib/graphql/queries/FETCH_CART_EMBROIDERY_CONFIG'
import { isItemEmbroiderableAndUnembroidered } from '../../lib/utils'
import { CartItem } from '../../types/figs'

import { EmbroideryProvider, UseEmbroideryProps, useEmbroideryDialog } from './EmbroideryProvider'

const KIT_IDENTITY_PROPERTY_KEYS = [
  '_kit',
  '_kitSku',
  '_kitId',
  '_setUid',
  '_swPromoEligible',
] as const

interface CartEmbroideryUpsellContextValue {
  activeItem: CartItem | undefined
  isItemEligibleForUpsell: (item: CartItem) => boolean
  openEmbroideryUpsellModal: (item: CartItem) => void
  closeUpsell: () => void
}

const CartEmbroideryUpsellContext = createContext<CartEmbroideryUpsellContextValue>({
  activeItem: undefined,
  isItemEligibleForUpsell: () => false,
  openEmbroideryUpsellModal: () => undefined,
  closeUpsell: () => undefined,
})

export const useCartEmbroideryUpsell = (): CartEmbroideryUpsellContextValue =>
  useContext(CartEmbroideryUpsellContext)

/**
 * Cart embroidery upsell singleton. Mounted once per cart surface.
 */
export const CartEmbroideryUpsellProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const { b2bStorefrontCollection } = useOrganization()
  // DTC only: B2B/Teams carts (portal or team store context) are excluded.
  const isDtc = !b2bStorefrontCollection

  const upsellFlag = useFeatureFlag<'true' | 'false'>({
    key: 'cart-embroidery-upsell',
    controlVariant: 'false',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isDtc,
  })

  const isUpsellEnabled = upsellFlag === 'true'

  const { cart } = useCart()

  // Track only the line key; the live item is re-derived from the cart on each
  // render so quantity/property changes (pending qty update, kit re-price) while
  // the modal is open aren't converted at a stale snapshot.
  const [activeItemKey, setActiveItemKey] = useState<string | undefined>(undefined)
  const activeItem = activeItemKey ? cart.items[activeItemKey] : undefined

  const isItemEligibleForUpsell = useCallback(
    (item: CartItem): boolean =>
      isUpsellEnabled && isDtc && isItemEmbroiderableAndUnembroidered(item),
    [isUpsellEnabled, isDtc]
  )

  const openEmbroideryUpsellModal = useCallback((item: CartItem) => {
    setActiveItemKey(item.key)
  }, [])

  const closeUpsell = useCallback(() => {
    setActiveItemKey(undefined)
  }, [])

  const value = useMemo(
    () => ({ activeItem, isItemEligibleForUpsell, openEmbroideryUpsellModal, closeUpsell }),
    [activeItem, isItemEligibleForUpsell, openEmbroideryUpsellModal, closeUpsell]
  )

  return (
    <CartEmbroideryUpsellContext.Provider value={value}>
      {children}
      {activeItem && (
        <CartEmbroideryUpsellModalLoader
          // Re-mount per line so the embroidery provider/state is reset between items.
          key={activeItem.key}
          activeItem={activeItem}
          onClose={closeUpsell}
        />
      )}
    </CartEmbroideryUpsellContext.Provider>
  )
}

/**
 * Mounted only while a line is active. Lazily fetches that line's full embroidery
 * config and, once it resolves, mounts the reused `EmbroideryModal` in cart mode.
 */
const CartEmbroideryUpsellModalLoader: FC<{
  activeItem: CartItem
  onClose: () => void
}> = ({ activeItem, onClose }) => {
  const { personalizedClient } = useGraphqlClients()
  const { addEmbroideryToExistingCartItem } = useCart()
  const [fetchEmbroideryConfig, { data, loading, error }] = useLazyQuery(
    FETCH_CART_EMBROIDERY_CONFIG,
    {
      client: personalizedClient,
    }
  )

  useEffect(() => {
    void fetchEmbroideryConfig({ variables: { sku: activeItem.sku } })
  }, [fetchEmbroideryConfig, activeItem.sku])
  const variant = data?.productCatalogVariants[0]
  const group = variant?.group

  const initialState = useMemo<UseEmbroideryProps | undefined>(() => {
    if (!variant || !group) return undefined
    return {
      status: loading ? 'pending' : error ? 'rejected' : 'resolved',
      productHandle: group.product.handle,
      productTitle: activeItem.title,
      productCategoryId: group.primaryCategory.id,
      productColorId: group.color?.id,
      productColorHexCodes: group.color?.hexCodes,
      productEmbroideryConfig: group.embroidery,
      embroideryPurpose: 'DTC',
      withPricing: false,
      enableEmbroideryPreferences: true,
      cartUpsell: {
        item: {
          quantity: activeItem.quantity,
          product: { handle: group.product.handle },
          variant: {
            shopifyId: variant.shopifyId,
            shopifyProductId: variant.shopifyProductId,
            sku: variant.sku,
            price: {
              amount: variant.price.amount,
              currencyCode: variant.price.currencyCode,
            },
            compareAtPrice: variant.compareAtPrice
              ? { amount: variant.compareAtPrice.amount }
              : undefined,
          },
          variantGroup: {
            siteWidePromoEligible: group.siteWidePromoEligible,
            finalSale: group.finalSale,
            primaryCategory: { id: group.primaryCategory.id },
          },
        },
        onAddEmbroidery: async embroideredItems => {
          const everyLineHasGuid =
            embroideredItems.length > 0 &&
            embroideredItems.every(embroideredItem => embroideredItem.properties?._guid)

          if (!everyLineHasGuid) return

          // Kit-grouping keys to carry onto the add-on lines so they stay grouped
          // with their garment. Empty for standalone (non-kit) items.
          const kitIdentityProperties = Object.fromEntries(
            KIT_IDENTITY_PROPERTY_KEYS.filter(key => activeItem.properties[key] !== undefined).map(
              key => [key, activeItem.properties[key]]
            )
          )
          const attributedItems = embroideredItems.map(embroideredItem => {
            // The garment line is the original re-added under the same SKU: copy all
            // its properties (incl. per-line discount). The embroidery/setup lines get
            // only kit grouping, never the garment's discount.
            const isConvertedGarmentLine = embroideredItem.item.sku === activeItem.sku
            return {
              ...embroideredItem,
              properties: {
                ...(isConvertedGarmentLine ? activeItem.properties : kitIdentityProperties),
                // Embroidery properties win over the inherited ones.
                ...embroideredItem.properties,
                // Tag the add-on lines (not the garment) as upsell-attributed.
                ...(isConvertedGarmentLine ? {} : { [EMBROIDERY_UPSELL_ATTRIBUTE_KEY]: 'true' }),
              },
            }
          })
          await addEmbroideryToExistingCartItem(attributedItems, activeItem.key)
        },
      },
    }
  }, [
    variant,
    group,
    loading,
    error,
    activeItem.title,
    activeItem.quantity,
    activeItem.key,
    activeItem.properties,
    activeItem.sku,
    addEmbroideryToExistingCartItem,
  ])

  // Wait until the config has resolved before mounting the provider/modal so the
  // embroidery state is initialized from real data rather than placeholders.
  if (!initialState) return null

  return (
    <EmbroideryProvider initialState={initialState}>
      <EmbroideryModal />
      <CartEmbroideryUpsellAutoOpener onDialogClosed={onClose} />
    </EmbroideryProvider>
  )
}

/**
 * Opens the embroidery dialog once the config resolves, and on dialog close
 * clears the active line so the upsell button re-surfaces if the line is still eligible.
 */
const CartEmbroideryUpsellAutoOpener: FC<{
  onDialogClosed: () => void
}> = ({ onDialogClosed }) => {
  const { open, isOpen } = useEmbroideryDialog()

  // used to ensure we've actually seen the modal open before we close it. Otherwise
  // the second effect below would fire on init and close the open modal immediately.
  const hasObservedOpenRef = useRef(false)

  useEffect(() => {
    void open()
  }, [open])

  useEffect(() => {
    if (isOpen) {
      hasObservedOpenRef.current = true
      return
    }
    // Only a true open→closed transition counts as a close.
    if (hasObservedOpenRef.current) {
      onDialogClosed()
    }
  }, [isOpen, onDialogClosed])

  return null
}
